import React, { useEffect, useState } from "react";
import { useMount } from "hooks/useMount";
import { Layout } from "layout/Layout";
import { Views } from "pages/Views";
import { Loading2 } from "components/Loading2/Loading2";
import { Nav } from "components/Nav/Nav";
import { Drawer } from "components/Drawer/Drawer";
import { Portals } from "portals/Portals";
import { useAtom, useGet, useSet } from "state/jotai";
import { logConfig, params_query, renderLayout, sessionTokenAtom } from "state/store.global";
import "./styles/fonts/fonts.css";
import "./styles/fonts/common.css";
import ContactDrawer from "components/Contact/ContactDrawer";
import BookViewingDrawer from "components/Contact/BookViewingDrawer";
import OpenHouseDrawer from "components/Contact/OpenHouseDrawer";
import { Toaster } from "components/ToastMessage/Toaster";
import { SnackbarProvider } from "components/ToastMessage/useSnackbar";
import { useBreak } from "hooks/useBreak";
import { useLocation } from "react-router-dom";
import { useCheckUserIdentifier } from "hooks/fetch/useCheckUserIdentifier";
import { HEART_SESSION, LOGGING_ID, SESSION_PAGE, START_SESSION ,GET_LOGGING_CONFIG} from "api/api";
import { useFetch } from "hooks/fetch/useFetch";
import { isProdDomain } from "logging/logging";

export const App = () => {
  const sessionToken = sessionStorage.getItem('sessionToken')
  const location = useLocation();
  const params = useGet(params_query);
  const setParams = useSet(params_query);
  const isDesktop = useBreak("md_up");
  const setLogConfig = useSet(logConfig)

  const getLogConfig = useFetch({ url: GET_LOGGING_CONFIG() });

  const [userId, setUserId] = useState(null);

  const PROJECT_ID = LOGGING_ID;
  const loggingEnabled = isProdDomain();
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  function generateSessionId() {
    return Array.from({ length: 4 })
      .map(() => Math.random().toString(16).substring(2, 10))
      .join("");
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Initialize sessionStorage user identifier
  useEffect(() => {

    if(getLogConfig?.D) {
      setLogConfig(getLogConfig.D)
    }

    let id = sessionStorage.getItem("user_identifier");
    if (!id) {
      id = `user-${generateSessionId()}`;
      sessionStorage.setItem("user_identifier", id);
    }
    setUserId(id);


  }, [logConfig]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Check uniqueness of identifier
  useCheckUserIdentifier(userId, {
    onDone: (result) => {
      const exists = result === true || result?.exists;
      if (exists) {
        const newId = `user-${generateSessionId()}`;
        sessionStorage.setItem("user_identifier", newId);
        setUserId(newId);
      } else {
      }
    },
  });

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Start session
  useEffect(() => {
   if (!userId || !loggingEnabled) return;

    const startSession = async () => {
      try {
        const response = await fetch(START_SESSION, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            projectID: PROJECT_ID,
            userIdentifier: userId,
          }),
        });

        if (!response.ok) {
          console.error("❌ Start session failed with status:", response.status);
          return;
        }

        const data = await response.json();

        if (data?.sessionId) {
          sessionStorage.setItem('sessionID', data.sessionId);
        } 
        if (data?.SessionToken) {
          sessionStorage.setItem('sessionToken', data.SessionToken);
        } 
      } catch (error) {
        console.error("❌ Failed to start session:", error);
      }
    };

    startSession();
  }, [userId]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 // Heartbeat
useEffect(() => {
   if (!sessionToken || !loggingEnabled) return;

  const sendHeartbeat = async () => {
    try {
      const res = await fetch(HEART_SESSION, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
         body: JSON.stringify({sessionToken}),
      });

      if (!res.ok) {
        console.error("❌ Heartbeat failed with status:", res.status);
      }
    } catch (err) {
      console.error("❌ Heartbeat failed:", err);
    }
  };

  // Send immediately and then repeat
  sendHeartbeat();
  const interval = setInterval(sendHeartbeat, 25000);
  return () => clearInterval(interval);
}, [ sessionToken]);

// Page logging
useEffect(() => {
   if (!sessionToken || !loggingEnabled) return;

  const logPage = async () => {
    try {
      const fullUrl = window.location.href;
      const res = await fetch(SESSION_PAGE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionToken,
          pageUrl: fullUrl,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!res.ok) {
        console.error("❌ Log page failed with status:", res.status);
      } else {
      }
    } catch (err) {
      console.error("❌ Failed to log page:", err);
    }
  };

  logPage();
}, [sessionToken, location]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Set params from URL
  useEffect(() => {
    if (!params && location.search.length) {
      setParams(location.search);
    }
  }, [location]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  useMount(() => {
      if (loggingEnabled) {
      log({ action: "app_opened" });
    }
  });

  const shouldRender = useGet(renderLayout);

  if (shouldRender)
    return (
      <SnackbarProvider>
        <Toaster />
        <Layout>
          {{
            Navigation: <Nav />,
            Views: <Views />,
            DrawersLeft: [
              <Drawer
                key="1"
                filter
                style={{
                  borderRadius: !isDesktop ? "0px" : "10px",
                  width: !isDesktop ? "100%" : "381px",
                }}
              />,
            ],
            DrawersRight: [<Drawer key="2" w="450" detail />],
          }}
        </Layout>
        <Portals />
        <ContactDrawer />
        <BookViewingDrawer />
        <OpenHouseDrawer />
      </SnackbarProvider>
    );

  return <Loading2 />;
};
